import React, { useState } from 'react'
import { OneInput, OnePopover } from '@yy/one-ui'
import { StepDown } from '@yy/one-icon'
import cls from 'classnames'
import { InputProps } from 'antd/lib/input'
import { withLabelFormItem, LabelHocProps } from '@common/oneclub/components'
import hidePasswordIcon from './hidePassword.svg'
import showPasswordIcon from './showPassword.svg'
import styles from './index.module.less'
import { useTranslation } from 'react-i18next'
import { useBoolean } from 'ahooks'
import { useIsH5 } from '../../Device'

interface PasswordInputProps extends InputProps {
  resetClassName?: boolean
  rules?:
    | boolean
    | {
        text: string
        validator: (pwd: string) => boolean
      }[]
}

const defaultRules = [
  {
    text: 'password.rules.length',
    validator: (pwd: string) => {
      return pwd?.length > 7 && pwd?.length < 21
    },
  },
  {
    text: 'password.rules.Aa',
    validator: (pwd) => {
      return /[A-Z]/.test(pwd) && /[a-z]/.test(pwd)
    },
  },
  {
    text: 'password.rules.num',
    validator: (pwd) => {
      return /\d/.test(pwd)
    },
  },
  {
    text: 'password.rules.specialCharacters',
    validator: (pwd) => {
      return '!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~'.split('').some((c) => pwd?.includes(c))
    },
  },
]

export const PasswordInputNormal: React.FC<PasswordInputProps> = (props) => {
  const { resetClassName, className, rules: _rules, onChange: propOnChange, onFocus: propOnFocus, onBlur: propOnBlur, ...otherProps } = props
  const rules = _rules ? (typeof _rules !== 'boolean' ? _rules : defaultRules) : null
  const isH5 = useIsH5()
  const [showPassword, setShowPassword] = useState(false)
  const { t } = useTranslation()
  const right = (
    <img
      className={cls(styles.hidePasswordIcon, otherProps.size === 'middle' ? styles.middle : '')}
      src={(showPassword && showPasswordIcon) || hidePasswordIcon}
      onClick={() => {
        setShowPassword((showPassword) => !showPassword)
      }}
    />
  )

  const [focus, actions] = useBoolean(false)

  const [checkRuleResult, setCheckRuleResult] = useState(rules?.map((r) => ({ text: r.text, checked: false })))
  const checkRules = (pwd: string) => {
    if (!rules?.length) return
    setCheckRuleResult(
      rules.map((r) => {
        return { text: r.text, checked: r.validator(pwd) }
      })
    )
  }
  const onChange: InputProps['onChange'] = (e) => {
    propOnChange(e)
    const value = e.target.value
    checkRules(value)
    actions.setTrue()
  }
  const onFocus = (e) => {
    propOnFocus?.(e)
    actions.setTrue()
  }
  const onBlur = (e) => {
    propOnBlur?.(e)
    actions.setFalse()
  }

  return isH5 ? (
    <div>
      <OneInput
        className={resetClassName ? className : cls(styles.pwd, className)}
        suffix={right}
        type={(showPassword && 'text') || 'password'}
        {...otherProps}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
      ></OneInput>
      {focus && !!checkRuleResult?.length ? (
        <div className={cls(styles.popContent, styles.h5)}>
          <div className={styles.title}>{t('password.must')}</div>
          {checkRuleResult?.map((c) => {
            return (
              <div className={cls(styles.item, c.checked ? styles.check : null)}>
                {c.checked ? <StepDown /> : <div className={styles.dot} />}
                {t(c.text)}
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  ) : (
    <OnePopover
      visible={focus && !!checkRuleResult?.length}
      content={
        <div className={styles.popContent}>
          <div className={styles.title}>{t('password.must')}</div>
          {checkRuleResult?.map((c) => {
            return (
              <div className={cls(styles.item, c.checked ? styles.check : null)}>
                {c.checked ? <StepDown /> : <div className={styles.dot} />}
                {t(c.text)}
              </div>
            )
          })}
        </div>
      }
    >
      <OneInput
        className={resetClassName ? className : cls(styles.pwd, className)}
        suffix={right}
        type={(showPassword && 'text') || 'password'}
        {...otherProps}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
      ></OneInput>
    </OnePopover>
  )
}

PasswordInputNormal.displayName = 'PasswordInputNormal'

export const PasswordInput = withLabelFormItem<PasswordInputProps & LabelHocProps>(PasswordInputNormal)
